/**
 * HTTP request helpers for BaseApiService
 * Extracted to reduce factory function size and enable reuse
 */

import { ContentType } from "../ContentType";
import { buildHeaders } from "./headerHelpers";
import { buildBody, fetchWithNetworkLogging, markBackgroundRequestError, sendRequest } from "./requestHelpers";
import type { ApiPayload } from "../types";

export interface HttpRequestOptions {
	url: URL;
	method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
	body?: ApiPayload;
	contentType?: ContentType;
	ref?: { response?: Response };
	fileName?: string;
	forceNoCache?: boolean;
	skipSessionFailureOnAuthError?: boolean;
	/** Background/polling call: failures are marked so factory `onError` handlers never toast. */
	background?: boolean;
}

/**
 * Generic HTTP request handler
 * Handles common request logic for all HTTP methods
 */
function shouldForceNoCache(forceNoCacheParam: boolean, forceNoCache: boolean): boolean {
	return Boolean(forceNoCacheParam || forceNoCache);
}

function buildFetchOptions(
	method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
	headers: Headers,
	body: ApiPayload | undefined,
	forceNoCache: boolean,
): RequestInit {
	const fetchOptions: RequestInit = { method, headers, credentials: "include" };
	if (body && method !== "GET") {
		fetchOptions.body = buildBody(body);
	}
	fetchOptions.cache = forceNoCache ? "no-store" : method === "GET" ? "default" : "default";
	return fetchOptions;
}

export async function makeHttpRequest<T>(options: HttpRequestOptions): Promise<T> {
	const {
		url,
		method,
		body,
		contentType = ContentType.JSON,
		ref,
		fileName,
		forceNoCache = false,
		skipSessionFailureOnAuthError,
		background,
	} = options;

	const request = async (forceNoCacheParam = false): Promise<Response> => {
		const headers = buildHeaders(contentType, url.pathname);
		const noCache = shouldForceNoCache(forceNoCacheParam, forceNoCache);
		if (noCache) {
			headers.set("Cache-Control", "no-cache");
			headers.set("Pragma", "no-cache");
		}
		const fetchOptions = buildFetchOptions(method, headers, body, noCache);
		return fetchWithNetworkLogging(url.toString(), fetchOptions, background ? { background: true } : undefined);
	};

	// For GET requests, provide forceNoCacheRequest function
	const forceNoCacheRequest = method === "GET" ? request : undefined;

	try {
		return await sendRequest<T>(() => request(forceNoCache), {
			ref,
			fileName,
			forceNoCacheRequest,
			skipSessionFailureOnAuthError,
		});
	} catch (error) {
		// HTTP-status failures of background calls must stay silent in factory catch handlers too.
		if (background) {
			markBackgroundRequestError(error);
		}
		throw error;
	}
}
